
import { useState, useCallback } from "react";
import { toast } from "sonner";
import { ApparelProduct } from "@/types/models";
import { CartItem } from "@/types/apparelTypes";

export function useApparelCart() {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ApparelProduct | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const handleProductSelect = useCallback((product: ApparelProduct) => {
    setSelectedProduct(product);
    setDetailsOpen(true);
  }, []);

  const addToCart = useCallback((product: ApparelProduct, quantity: number = 1, size?: string) => {
    // DEBUG: Log addToCart call
    console.log('addToCart called with:', { product: product.name, quantity, size });
    console.log('Current cart before adding:', cart);
    
    // Check if product is out of stock
    if (product.has_inventory && 
        product.inventory_count !== null && 
        product.inventory_count <= 0) {
      toast.error("Product is out of stock");
      return;
    }
    
    // Check if size is required but not selected
    if (product.requires_size && !size) {
      toast.error("Please select a size");
      return;
    }
    
    // Check if already in cart - update quantity if so
    const existingIndex = cart.findIndex(item => 
      item.product.id === product.id && 
      ((!product.requires_size) || (item.size === size))
    );
    
    if (existingIndex >= 0) {
      const updatedCart = [...cart];
      updatedCart[existingIndex].quantity += quantity;
      setCart(updatedCart);
      console.log('Updated existing cart item, new cart:', updatedCart);
    } else {
      const newCart = [...cart, { product, quantity, size }];
      setCart(newCart);
      console.log('Added new item to cart, new cart:', newCart);
    }
    
    toast.success("Added to cart");
    setDetailsOpen(false);
  }, [cart]);

  const handleRemoveFromCart = useCallback((index: number) => {
    const newCart = [...cart];
    newCart.splice(index, 1);
    setCart(newCart);
  }, [cart]);

  const handleUpdateQuantity = useCallback((index: number, quantity: number) => {
    if (quantity < 1) return;
    
    const newCart = [...cart];
    newCart[index].quantity = quantity;
    setCart(newCart);
  }, [cart]);

  const handleUpdateSize = useCallback((index: number, size: string) => {
    const newCart = [...cart];
    newCart[index].size = size;
    setCart(newCart);
  }, [cart]);

  const handleCheckout = useCallback(() => {
    setCartOpen(false);
    setCheckoutOpen(true);
  }, []);

  const handleOrderSuccess = useCallback(() => {
    setCheckoutOpen(false);
    setCart([]);
    toast.success("Order placed successfully!");
  }, []);

  const getTotalItems = useCallback(() => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  }, [cart]);

  const cartSubtotal = useCallback(() => {
    return cart.reduce((total, item) => total + (item.product.price_cents * item.quantity), 0);
  }, [cart]);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  return {
    cart,
    selectedProduct,
    detailsOpen,
    cartOpen,
    checkoutOpen,
    setDetailsOpen,
    setCartOpen,
    setCheckoutOpen,
    handleProductSelect,
    addToCart,
    handleRemoveFromCart,
    handleUpdateQuantity,
    handleUpdateSize,
    handleCheckout,
    handleOrderSuccess,
    getTotalItems,
    cartSubtotal,
    clearCart
  };
}
